import React, { useState, useEffect, useCallback } from 'react';
import { BaseGrid } from './BaseGrid';
import { StatefulDataTable, StatefulDataTableProps } from 'baseui/data-table';
import { Input } from 'baseui/input';
import { InlineFilters, FilterState } from './InlineFilters';
import { Flex } from './Flex';

import { styled } from 'baseui';

// Type assertion to handle BaseUI compatibility issues
const InputComponent = Input as any;

const SearchContainer = styled('div', {
  padding: '16px',
  borderBottom: '1px solid #F0F0F0',
  backgroundColor: '#F9FAFB',
});

const SearchAndFiltersContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
  flexWrap: 'wrap',
});

const SearchBarContainer = styled('div', {
  minWidth: '507px',
  maxWidth: '676px',
  flex: '0 0 auto',
});

const FiltersContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flex: '0 0 auto',
});

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

type BaseGridWithSearchProps = {
  onSearch?: (searchTerm: string) => void;
  searchPlaceholder?: string;
  debounceMs?: number;
  loading?: boolean;
  rows?: Array<any>;
  columns?: Array<any>;
  pagination?: {
    numPages: number;
    currentPage: number;
    totalRecords?: number;
    currentLimit?: number;
    onPageChange: ({ nextPage }: { nextPage: number }) => void;
  };
  emptyMessage?: string;
  loadingMessage?: string;
  showPaginationOptions?: boolean;
  onRowClick?: (rowData: any) => void;
  sortable?: boolean;
  onColumnSort?: (columnTitle: string) => void;
  // Filter props
  filters?: FilterState;
  onFiltersChange?: (filters: FilterState) => void;
  statusOptions?: Array<{ label: string; id: string }>;
  destinationOptions?: Array<{ label: string; id: string }>;
  typeOptions?: Array<{ label: string; id: string }>;
};

export const BaseGridWithSearch: React.FC<BaseGridWithSearchProps> = ({
  onSearch,
  searchPlaceholder = "Search...",
  debounceMs = 500,
  loading = false,
  rows = [],
  columns = [],
  pagination,
  emptyMessage = "No data available",
  loadingMessage = "Loading...",
  showPaginationOptions = false,
  onRowClick,
  sortable = false,
  onColumnSort,
  filters,
  onFiltersChange,
  statusOptions = [],
  destinationOptions = [],
  typeOptions = [],
  ...otherProps
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [searchTerm, debounceMs]);

  // Call onSearch when debounced term changes
  useEffect(() => {
    if (onSearch) {
      onSearch(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, onSearch]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    
    // Si se limpia completamente el input, trigger inmediato de búsqueda vacía
    if (newValue === '' && onSearch) {
      onSearch('');
    }
  }, [onSearch]);

  // Manejar cuando se usa el botón clear del input
  const handleClear = useCallback(() => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    if (onSearch) {
      onSearch('');
    }
  }, [onSearch]);


  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <SearchContainer>
        <SearchAndFiltersContainer>
          <SearchBarContainer>
            <InputComponent
              value={searchTerm}
              onChange={handleSearchChange}
              onClear={handleClear}
              placeholder={searchPlaceholder}
              startEnhancer={<SearchIcon />}
              clearable
              overrides={{
                Root: {
                  style: {
                    borderRadius: '8px',
                    width: '100%',
                    height: '48px',
                  }
                },
              Input: {
                style: {
                  backgroundColor: '#F3F3F3',
                  borderColor: 'transparent',
                  height: '48px',
                  ':hover': {
                    backgroundColor: '#F3F3F3',
                    borderColor: '#E2E8F0',
                  },
                  ':focus': {
                    backgroundColor: '#fff',
                    borderColor: '#3182CE',
                  },
                }
              },
              InputContainer: {
                style: {
                  backgroundColor: '#F3F3F3',
                  borderColor: 'transparent',
                  height: '48px',
                  ':hover': {
                    backgroundColor: '#fff',
                    borderColor: '#E2E8F0',
                  },
                  ':focus-within': {
                    backgroundColor: '#fff',
                    borderColor: '#3182CE',
                  }
                }
              }
            }}
            />
          </SearchBarContainer>
          
          {filters && onFiltersChange && (
            <FiltersContainer>
              <InlineFilters
                filters={filters}
                onFiltersChange={onFiltersChange}
                statusOptions={statusOptions}
                destinationOptions={destinationOptions}
                typeOptions={typeOptions}
              />
            </FiltersContainer>
          )}
        </SearchAndFiltersContainer>
      </SearchContainer>
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <BaseGrid
          loading={loading}
          loadingMessage={loadingMessage}
          columns={columns}
          rows={rows}
          searchable={false} // Disable built-in search since we handle it
          filterable={false} // BaseUI doesn't have built-in filtering
          sortable={sortable}
          emptyMessage={emptyMessage}
          pagination={pagination}
          onRowDoubleClick={onRowClick}
          onColumnSort={onColumnSort}
          {...otherProps}
        />
      </div>
    </div>
  );
}; 